// React
import React, { useEffect, useState } from 'react';

// Containers Injections
import {
  ModalHasSchedule,
  ModalBalanceHasReduced,
} from '../../containers/componentsInjection';
import { BillsToPayInjection } from '../../containers/billsToPayInjection';

// Yup & Formik
import * as Yup from 'yup';
import { useFormik } from 'formik';

// View
import { BillsToPayFormPage } from './view';

// Helpers
import { APIClient } from '../../../../helpers/api_helper';

/**
 * @ALERT - MODULE RULES
 *
 * In this module some business rules have been implemented which are relevant to know:
 *
 * 1. The "repetition" state defines the format of the body to be submitted.
 *
 * 2. There is an action in the listing called "confirm", which dynamically changes the key of
 * the "balance" and "expirationDate" variables to be submitted.
 *
 * 3. In the case of editing or confirmation, if a variable called "financial_schedule_id" comes
 * from the API, a modal is opened to collect a response from the user and add it to the valuesToSubmit
 * as "schedule_batch".
 *
 * 4. In case of confirmation, if the user changes the value of "balance" to less, a modal is opened
 * to collect a true/false information from the user called "partial". If "partial" is true, the modal
 * for creating an account is opened with pre-filled values to generate a new bill with remains.
 */

export const BillsToPayForm = ({
  toggle,
  isOpen,
  isPartial,
  billsByHash,
  setIsPartial,
  selectedFile,
  setBillsByHash,
  isConfirmAction,
  setSelectedFile,
  setIsConfirmAction,
}) => {
  const [repetition, setRepetition] = useState('u');
  const [dataToSubmit, setDataToSubmit] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [hasScheduleId, setHasScheduleId] = useState(false);
  const [hasBalanceReduced, setHasBalanceReduced] = useState(false);

  const api = new APIClient();
  const formatter = new Intl.NumberFormat('pt-BR');
  let currentDate = new Date().toJSON().slice(0, 10);

  const {
    offices,
    accounts,
    getOffices,
    getAccounts,
    costCenters,
    getCostCenter,
    suppliersLookup,
    createBillsToPay,
    updateBillsToPay,
    getSuppliersLookup,
    categoryFinanceOrganized,
    getFinancialCategoriesOrganized,
  } = BillsToPayInjection();

  // toggles
  const toggleHasScheduleId = () => {
    setHasScheduleId((prevState) => !prevState);
  };

  const toggleHasBalanceReduced = () => {
    setHasBalanceReduced((prevState) => !prevState);
  };

  // helpers
  function verifyIfBalanceHasReduced(initialBalance, balance) {
    if (balance < initialBalance) {
      return true;
    } else {
      return false;
    }
  }

  // GET calls
  const getPaymentMethodsOptions = async () => {
    await api.get('/register/payment-method').then((payment) => {
      setPaymentMethods(payment);
    });
  };

  // handle's functions
  const handleEdit = (id, values) => {
    //  in cases of editing where there is "financial_schedule_id", a modal must be opened before editing is done. (a new value must be added to the body - "schedule_batch")
    if (billsByHash.financial_schedule_id) {
      toggleHasScheduleId();
    } else {
      updateBillsToPay(id, values);
      validation.resetForm();
      toggle();
    }
  };

  const handleEditWhenHasSchedule = (scheduleType, values) => {
    updateBillsToPay(billsByHash.id, {
      ...values,
      schedule_batch: scheduleType,
    });
    validation.resetForm();
    toggle();
  };

  const handleConfirmation = (id, values) => {
    if (
      verifyIfBalanceHasReduced(
        parseInt(billsByHash.value),
        values.payment_value
      )
    ) {
      toggleHasBalanceReduced();
    } else {
      updateBillsToPay(id, values);
      validation.resetForm();
      toggle();
    }
  };

  const handleConfirmWhenBalanceHasReduce = (partial, values) => {
    updateBillsToPay(billsByHash.id, {
      ...values,
      partial,
    });
    toggleHasBalanceReduced();
    toggle();

    if (partial) {
      setIsPartial(true);
      setIsConfirmAction(false);
      setBillsByHash({
        ...values,
        value: parseInt(billsByHash.value) - values.payment_value,
        due_date: currentDate,
        financial_account_name: billsByHash.financial_account_name,
        financial_category_name: billsByHash.financial_category_name,
        financial_center_name: billsByHash.financial_center_name,
        office_name: billsByHash.office_name,
        payment_method_name: billsByHash.payment_method_name,
        person_name: billsByHash.person_name,
        description: `${billsByHash.description} (resíduo)`,
      });
      setTimeout(() => {
        toggle();
      }, 2000);
    } else {
      validation.resetForm();
    }
  };

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      // principal
      balance: billsByHash ? parseInt(billsByHash.value) : null,
      expirationDate: !billsByHash
        ? ''
        : isConfirmAction
        ? currentDate
        : billsByHash?.due_date,
      documentNumber: billsByHash ? billsByHash.number : '',
      repetition: {
        value: 'u',
        label: 'Única',
      },
      periodicity: {
        value: 'm',
        label: 'Mensal',
      },
      installmentsAmount: '',
      installmentValue: true,
      description: billsByHash ? billsByHash.description : '',
      assignor: billsByHash
        ? {
            value: billsByHash.person_id,
            label: billsByHash.person_name,
          }
        : {
            value: '',
            label: '',
          },
      category: billsByHash
        ? {
            value: billsByHash.financial_category_id,
            label: billsByHash.financial_category_name,
          }
        : {
            value: '',
            label: '',
          },
      account: billsByHash
        ? {
            value: billsByHash.financial_account_id,
            label: billsByHash.financial_account_name,
          }
        : {
            value: '',
            label: '',
          },
      costCenter: billsByHash
        ? {
            value: billsByHash.financial_center_id,
            label: billsByHash.financial_center_name,
          }
        : {
            value: '',
            label: '',
          },
      paymentMethod: billsByHash
        ? {
            value: billsByHash.payment_method_id,
            label: billsByHash.payment_method_name,
          }
        : {
            value: '',
            label: '',
          },
      observations: billsByHash ? billsByHash.notes : '',
      office: billsByHash
        ? {
            value: billsByHash.office_id,
            label: billsByHash.office_name,
          }
        : {
            value: '',
            label: '',
          },
      frequency: {
        value: '',
        label: '',
      },
      scheduleType: '',
    },

    validationSchema: Yup.object().shape({
      balance: Yup.string().nullable().required(),
      expirationDate: Yup.string().nullable().required(),
      documentNumber: Yup.string().nullable(),
      repetition: Yup.object().shape({
        label: Yup.string(),
        value: Yup.string(),
      }),
      periodicity: Yup.object().shape({
        label: Yup.string(),
        value: Yup.string(),
      }),
      installmentsAmount: Yup.string().nullable(),
      installmentValue: Yup.boolean(),
      description: Yup.string().nullable().required(),
      assignor: Yup.object().shape({
        label: Yup.string().nullable(),
        value: Yup.string().nullable(),
      }),
      category: Yup.object().shape({
        label: Yup.string(),
        value: Yup.string().required(),
      }),
      account: Yup.object().shape({
        label: Yup.string().nullable(),
        value: Yup.string().nullable(),
      }),
      office: Yup.object().shape({
        label: Yup.string(),
        value: Yup.string().required(),
      }),
      costCenter: Yup.object().shape({
        label: Yup.string().nullable(),
        value: Yup.string().nullable(),
      }),
      paymentMethod: Yup.object().shape({
        label: Yup.string().nullable(),
        value: Yup.string().nullable(),
      }),
      observations: Yup.string().nullable(),
      frequency: Yup.object().shape({
        label: Yup.string().nullable(),
        value: Yup.string().nullable(),
      }),
      scheduleType: Yup.string(),
    }),

    onSubmit: (values) => {
      let dynamicVarNameToBalance = isConfirmAction ? 'payment_value' : 'value';
      let dynamicVarNameToExpirationDate = isConfirmAction
        ? 'payment_date'
        : 'due_date';

      let valuesToSubmit = {
        type: 'd',
        financial_account_id: values.account.value || null,
        financial_category_id: values.category.value,
        financial_center_id: values.costCenter.value || null,
        payment_method_id: values.paymentMethod.value || null,
        office_id: values.office.value,
        number: values.documentNumber || null,
        description: values.description,
        person_id: values.assignor.value,
        notes: values.observations || null,
        file: selectedFile[0] || null,
      };

      valuesToSubmit[dynamicVarNameToBalance] = values.balance;
      valuesToSubmit[dynamicVarNameToExpirationDate] = values.expirationDate;

      // for each type of repetition ('u','p' or 'f'), there is a different form body
      if (repetition !== 'u') {
        if (repetition === 'p') {
          valuesToSubmit = {
            ...valuesToSubmit,
            frequency: 1,
            interval: values.periodicity.value,
            qty: values.installmentsAmount || null,
          };
        } else {
          valuesToSubmit = {
            ...valuesToSubmit,
            frequency: values.frequency.value || null,
            interval: values.periodicity.value,
            qty: -1,
          };
        }
      }

      // here is the logic for actually sending the data.
      if (billsByHash && !isPartial) {
        setDataToSubmit(valuesToSubmit);
        if (isConfirmAction) {
          handleConfirmation(billsByHash.id, valuesToSubmit);
        } else {
          handleEdit(billsByHash.id, valuesToSubmit);
        }
      } else {
        createBillsToPay({
          ...valuesToSubmit,
          repeat: repetition,
        });
        toggle();
        setIsPartial(false);
        validation.resetForm();
      }
      setRepetition('u');
    },
  });

  useEffect(() => {
    getOffices();
    getAccounts();
    getCostCenter();
    getSuppliersLookup();
    getPaymentMethodsOptions();
    getFinancialCategoriesOrganized('d');
  }, []);

  return (
    <>
      <BillsToPayFormPage
        // data
        offices={offices}
        accounts={accounts}
        costCenter={costCenters}
        suppliesList={suppliersLookup}
        paymentMethods={paymentMethods}
        isConfirmAction={isConfirmAction}
        categories={categoryFinanceOrganized}
        isUpdate={!isPartial && billsByHash}
        lastUpdated={billsByHash?.last_update}
        // utils
        isOpen={isOpen}
        toggle={toggle}
        formatter={formatter}
        validation={validation}
        repetition={repetition}
        selectedFile={selectedFile}
        setRepetition={setRepetition}
        setSelectedFile={setSelectedFile}
      />

      <ModalHasSchedule
        isOpen={hasScheduleId}
        validation={validation}
        toggle={toggleHasScheduleId}
        handleSubmitHasSchedule={(scheduleType) => {
          handleEditWhenHasSchedule(scheduleType, dataToSubmit);
        }}
      />

      <ModalBalanceHasReduced
        validation={validation}
        isOpen={hasBalanceReduced}
        toggle={toggleHasBalanceReduced}
        handleSubmit={(partial) => {
          handleConfirmWhenBalanceHasReduce(partial, dataToSubmit);
        }}
      />
    </>
  );
};
